"use client"

import { type RefObject, useRef, useState } from "react"
import { useGSAP } from "@gsap/react"
import cn from "classnames"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

import { useScroll } from "~/components/foundations/ScrollProvider"
import buttonS from "~/components/primitives/Button/PrimaryButton.module.scss"
import { PerspectiveCard } from "~/components/primitives/PerspectiveCard/PerspectiveCard"
import { useMasonryLayout } from "~/hooks/useMasonryLayout"
import { useMatchMedia } from "~/hooks/useMatchMedia"
import type {
  AdaptedPerspectiveCard,
  AdaptedPerspectiveListing,
} from "~/server/adaptors/pages/perspectiveListing"

import s from "./PerspectiveListing.module.scss"

gsap.registerPlugin(ScrollTrigger)

const PAGE_SIZE = 100

type PerspectiveListingProps = {
  data: AdaptedPerspectiveListing
}

type VisiblePerspectiveCard = AdaptedPerspectiveCard & {
  originalIndex: number
}

export const PerspectiveListing = ({ data }: PerspectiveListingProps) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const sectionRef = useRef<HTMLElement>(null)

  const prefersReducedMotion = useMatchMedia("(prefers-reduced-motion: reduce)")

  const { lenis } = useScroll()

  const visibleEntries: VisiblePerspectiveCard[] = data.entries
    .slice(0, visibleCount)
    .map((entry, originalIndex) => ({ ...entry, originalIndex }))
  const hasMoreEntries = visibleCount < data.entries.length

  const handleLoadMore = () => {
    setVisibleCount((count) => Math.min(count + PAGE_SIZE, data.entries.length))
  }

  const masonryContainerRef = useRef<HTMLElement>(null)

  const { layoutVersion, heightDiff } = useMasonryLayout({
    containerRef: masonryContainerRef,
    columns: 2,
    balanceCols: prefersReducedMotion,
  })

  useGSAP(
    () => {
      if (!lenis) return

      const mm = gsap.matchMedia()

      mm.add(
        "(prefers-reduced-motion: no-preference) and (min-width: 768px)",
        () => {
          const colOne =
            masonryContainerRef.current?.querySelectorAll(`[data-column="0"]`)
          if (!colOne?.length) return

          colOne.forEach((target) => {
            gsap.fromTo(
              target,
              { y: 0 },
              {
                y: () => -1 * (heightDiff ?? 0),
                ease: "none",
                scrollTrigger: {
                  trigger: masonryContainerRef.current,
                  scroller: lenis.rootElement,
                  start: "top top",
                  end: "bottom bottom",
                  scrub: true,
                },
              },
            )
          })
        },
      )
    },
    {
      scope: masonryContainerRef,
      dependencies: [lenis, layoutVersion, heightDiff],
      revertOnUpdate: true,
    },
  )

  return (
    <section
      ref={sectionRef}
      className={cn(s.listings, "container")}
      aria-label={data.title}
    >
      <ul
        className={cn(s.grid, "masonry")}
        ref={masonryContainerRef as RefObject<HTMLUListElement>}
      >
        {visibleEntries.map((entry) => {
          return (
            <li key={entry.slug} className={cn(s.card, "masonry-item")}>
              {/* <FadeIn element="div"> */}
              <PerspectiveCard {...entry} />
              {/* </FadeIn> */}
            </li>
          )
        })}
      </ul>
      {hasMoreEntries && (
        <button
          aria-label="Load more articles"
          className={cn(buttonS.primaryButton, buttonS.dark, s.loadMore)}
          onClick={handleLoadMore}
        >
          Load more
        </button>
      )}
    </section>
  )
}
